import { publicationUpload } from '@/utils/aws';
import ClientService from '@/services/client/client';
import ProgramService from '@/services/program/program';
import { publication } from '@/store/client/types';
import { v4 as uuidv4 } from 'uuid';
import { slugify } from '@/utils/strings';
import { ClientSport } from '@/store/programs/types';
import { LandingPageData, ProgramPayload } from '@/components/types';

// Constants
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'webm', 'm4v'] as const; // Same extentions on the DP dashboard app > constants > VideoExts
const BACKUP_FILE_PREFIX = 'data_';
const LIVE_FILE_NAME = 'data_live.json';
const CONTENT_TYPE_JSON = 'application/json';

const publishProgramToClientPublications = async (
  clientId: number,
  programId: string | number,
  programUrl: string,
  tenantId: string,
  clientSport: any
): Promise<boolean> => {
  try {
    const { s3BucketUri } = await ClientService.fetchPublicationSettings(clientId);
    // backup current school publications page on S3
    await backupPublicationsPage(s3BucketUri, tenantId);
    // add or update Digital Program to school's publication page on S3
    await publishProgramToPublicationsPage(programId, programUrl, s3BucketUri, tenantId, clientSport);

    return Promise.resolve(true);
  } catch (error) {
    console.error('Error publishing program to client publications:', error);
    return Promise.reject(
      error?.response || {
        message: error instanceof Error ? error.message : 'Failed to publish program',
      }
    );
  }
};

const backupPublicationsPage = async (s3BucketUri: string, tenantId: string): Promise<boolean> => {
  try {
    const publicationPage = await ClientService.fetchPublicationPage(s3BucketUri);
    const publicationPageBlob = new Blob([JSON.stringify(publicationPage)], {
      type: CONTENT_TYPE_JSON,
    });

    const fileName = `${BACKUP_FILE_PREFIX}${Date.now()}.json`;
    const result = await publicationUpload(publicationPageBlob, tenantId, fileName);

    if (result.status !== 200) {
      throw new Error('Backup upload failed with status: ' + result.status);
    }
    return Promise.resolve(true);
  } catch (error) {
    console.error('Error creating backup:', error);
    throw new Error('A backup of the school publications could not be created');
  }
};

const getImagePath = (landingPage?: LandingPageData): string | undefined => {
  if (!landingPage?.media?.url) return landingPage?.image?.url;
  const fileExtension = landingPage.media.url.split('.').pop()?.toLowerCase();
  const isVideo = fileExtension && VIDEO_EXTENSIONS.includes(fileExtension as any);
  if (isVideo) return landingPage.media.videoTypeDetails?.thumbnailUrl || landingPage.image?.url;
  return landingPage.media.url;
};

const createPublicationObject = (
  program: ProgramPayload,
  programUrl: string,
  clientSport?: ClientSport
): publication => {
  const now = new Date().toISOString();
  const publishDate = new Date(program.publishDate).toDateString();

  const newPublication: publication = {
    created_on: now,
    description: '',
    id: uuidv4(),
    file: {
      image: getImagePath(program.landingPage),
      title: program.title,
      description: `Author: LEARFIELD Digital Publications, Catalog: ${program.title} Published: ${publishDate}`,
      url: programUrl,
      embed_url: '',
      slug: slugify(program.title),
    },
    image: '',
    title: program.title,
    updated_on: now,
    url: '',
    sport: null,
    sports: [],
  };

  if (clientSport) {
    newPublication.sports = [
      {
        id: program.sport.clientSportId,
        shortname: clientSport.shortName,
        slug: slugify(clientSport.name),
        title: clientSport.name,
      },
    ];
  }

  return newPublication;
};

const publishProgramToPublicationsPage = async (
  programId: string | number,
  programUrl: string,
  s3BucketUri: string,
  tenantId: string,
  clientSport: ClientSport
): Promise<boolean> => {
  try {
    const program = await ProgramService.fetchProgram(programId);
    const newPublication = createPublicationObject(program, programUrl, clientSport);

    const publicationPage = await ClientService.fetchPublicationPage(s3BucketUri);
    const publicationIndex = publicationPage.publications.findIndex(
      (publication: publication) => publication.file.url === newPublication.file.url
    );

    if (publicationIndex > -1) {
      // Update existing publication
      publicationPage.publications[publicationIndex] = {
        ...publicationPage.publications[publicationIndex],
        ...newPublication,
        created_on: publicationPage.publications[publicationIndex].created_on, // Keep original creation date
      };
    } else {
      // Add new publication at the beginning
      publicationPage.publications.unshift(newPublication);
    }

    const publicationPageBlob = new Blob([JSON.stringify(publicationPage)], {
      type: CONTENT_TYPE_JSON,
    });

    const uploadResult = await publicationUpload(publicationPageBlob, tenantId, LIVE_FILE_NAME);

    if (uploadResult.status !== 200) {
      throw new Error('Upload failed with status: ' + uploadResult.status);
    }

    return Promise.resolve(true);
  } catch (error) {
    console.error('Error publishing program to publications page:', error);
    throw new Error("The school's publications could not be updated");
  }
};

export { publishProgramToClientPublications };
